import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Barcode, ArrowRight, ShieldAlert, X } from 'lucide-react';
import { PermissionsProvider, usePermissions } from './PassportPermissions';
import { BarcodePrintModal } from './PassportManagement';

const createSampleBarcodePassport = () => ({
    PassportID: 1,
    NameArabic: 'نموذج تجربة الطباعة',
    PassportNumber: 'A1234567',
    NationalID: '29901011234567',
    CompanyID: null,
    DelegateID: null,
    PassportProfessionID: null,
    PhoneNumbers: ['01000000000'],
    PreviousCompanies: [],
    MedicalExamStatus: 'لائق',
    NetWorkDate: new Date().toISOString(),
    ApplicationNumber: '12345',
    FingerprintDate: '',
    VisaDate: '',
    TravelDate: ''
});

const ToastStack = ({ alerts, onClose }) => (
    <div className="fixed top-4 right-4 z-[10080] space-y-2" dir="rtl">
        {alerts.map(alert => (
            <div
                key={alert.id}
                className={`min-w-[280px] max-w-md rounded-2xl border px-4 py-3 shadow-xl flex items-start gap-3 ${
                    alert.type === 'success'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                        : alert.type === 'warning'
                            ? 'bg-amber-50 border-amber-200 text-amber-900'
                            : alert.type === 'error'
                                ? 'bg-red-50 border-red-200 text-red-900'
                                : 'bg-blue-50 border-blue-200 text-blue-900'
                }`}
            >
                <div className="font-bold flex-1 leading-6 text-sm">{alert.message}</div>
                <button onClick={() => onClose(alert.id)} className="shrink-0 opacity-70 hover:opacity-100">
                    <X size={16} />
                </button>
            </div>
        ))}
    </div>
);


const BarcodePageScrollStyle = () => (
    <style>{`
        html,
        body,
        #root {
            min-height: 100%;
        }

        body {
            overflow-y: auto !important;
            overscroll-behavior-y: auto !important;
        }

        .barcode-settings-page-shell {
            min-height: 100vh;
            overflow: visible !important;
            touch-action: pan-y;
        }

        .barcode-settings-page-shell,
        .barcode-settings-page-shell * {
            scrollbar-width: auto;
        }

        .barcode-settings-page-shell ::-webkit-scrollbar {
            width: 12px;
            height: 12px;
        }

        .barcode-settings-page-shell ::-webkit-scrollbar-thumb {
            background: rgba(100, 116, 139, 0.45);
            border-radius: 999px;
            border: 3px solid transparent;
            background-clip: content-box;
        }

        .barcode-settings-page-shell ::-webkit-scrollbar-thumb:hover {
            background: rgba(51, 65, 85, 0.62);
            border: 3px solid transparent;
            background-clip: content-box;
        }
    `}</style>
);


const canScrollInsideElement = (element, deltaY, rootElement) => {
    if (!element || !rootElement || typeof window === 'undefined') return false;

    let current = element;
    while (current && current !== rootElement && current.nodeType === 1) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY || '';
        const canHaveOwnScroll = /(auto|scroll|overlay)/.test(overflowY);
        const hasVerticalScroll = current.scrollHeight > current.clientHeight + 1;

        if (canHaveOwnScroll && hasVerticalScroll) {
            const atTop = current.scrollTop <= 0;
            const atBottom = current.scrollTop + current.clientHeight >= current.scrollHeight - 1;
            const canScrollUp = deltaY < 0 && !atTop;
            const canScrollDown = deltaY > 0 && !atBottom;
            if (canScrollUp || canScrollDown) return true;
        }

        current = current.parentElement;
    }

    return false;
};

const BarcodeSettingsPageContent = () => {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const [alerts, setAlerts] = React.useState([]);
    const samplePassport = React.useMemo(() => createSampleBarcodePassport(), []);

    const handlePageWheel = React.useCallback((event) => {
        if (!event || !event.deltaY || event.ctrlKey || typeof window === 'undefined') return;

        const rootElement = event.currentTarget;
        const targetElement = event.target;

        if (canScrollInsideElement(targetElement, event.deltaY, rootElement)) return;

        event.preventDefault();
        window.scrollBy({
            top: event.deltaY,
            left: event.deltaX || 0,
            behavior: 'auto'
        });
    }, []);

    const showAlert = React.useCallback((message, type = 'info') => {
        const id = Date.now() + Math.random();
        setAlerts(prev => [...prev, { id, message, type }]);
        window.setTimeout(() => {
            setAlerts(prev => prev.filter(alert => alert.id !== id));
        }, 4500);
    }, []);

    const closeAlert = React.useCallback((id) => {
        setAlerts(prev => prev.filter(alert => alert.id !== id));
    }, []);

    const goBack = React.useCallback(() => {
        navigate('/passports');
    }, [navigate]);

    const canManageBarcodePrinting = hasPermission('ضبط الباركود وطرق الطباعة');

    if (!canManageBarcodePrinting) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-white flex items-center justify-center p-4" dir="rtl">
                <div className="w-full max-w-lg bg-white border border-slate-200 rounded-[2rem] shadow-2xl p-7 text-center">
                    <div className="w-16 h-16 rounded-3xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
                        <ShieldAlert size={32} />
                    </div>
                    <h1 className="text-2xl font-black text-slate-900 mb-2">لا توجد صلاحية لضبط الباركود</h1>
                    <p className="text-slate-500 leading-7 mb-6">
                        فعّل صلاحية <b>ضبط الباركود وطرق الطباعة</b> من صفحة المستخدمين، ثم افتح الصفحة مرة أخرى.
                    </p>
                    <button
                        onClick={goBack}
                        className="bg-slate-900 hover:bg-black text-white px-5 py-3 rounded-2xl font-black inline-flex items-center gap-2"
                    >
                        <ArrowRight size={18} />
                        رجوع للجوازات
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="barcode-settings-page-shell" dir="rtl" onWheelCapture={handlePageWheel}>
            <BarcodePageScrollStyle />
            <ToastStack alerts={alerts} onClose={closeAlert} />
            <div className="hidden">
                <Barcode />
            </div>
            <BarcodePrintModal
                isOpen
                onClose={goBack}
                passport={samplePassport}
                companies={[]}
                delegates={[]}
                professions={[]}
                showAlert={showAlert}
                settingsOnly
                pageMode
            />
        </div>
    );
};

export default function BarcodeSettingsPage() {
    return (
        <PermissionsProvider>
            <BarcodeSettingsPageContent />
        </PermissionsProvider>
    );
}
